import { QueryResult } from '@apollo/client'
import { createStrictContext } from '../../../../../../../utils/createContext'
import {
  EditRequestForInformationMutationFn,
  GetRequestsForInformationQuery,
  GetRfiTrackerTableMetadataQuery,
  GetRfiTrackerTableMetadataQueryVariables,
  useEditRequestForInformationMutation,
  useGetRequestsForInformationQuery,
  useGetRfiTrackerTableMetadataQuery,
} from '../../../../../../../schema'
import { useGlobalState } from '../../../../../../../context/GlobalStateContext'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from '../../../../../../../utils/defaults'
import { useOptions } from '../../../../../../../components/CIMSSelect/hooks/useOptions'
import { DefaultOptionType } from '../../../../../../../components/CIMSSelect/types'
import {
  UseCIMSModalValues,
  useCIMSModalValues,
} from '../../../../../../../components/CIMSModal/hooks/useCIMSModal'
import { DraftSubmitRFIModalData } from './components/RFITrackerTable/components/DraftSubmitRFIModal'
import { simpleEditNotification } from '../../../../../../../hooks/notificationHandler'
import { DisciplineResponseModalData } from './components/RFITrackerTable/components/DisciplineResponseModal'
import { useExternalLinks } from '../../../../../../../components/CIMSTable/common/hooks/useExternalLinks'
import { SharePointParentFolder } from 'shared'
import { UseSharePointExternalLinks } from '../../../../../../../components/CIMSTable/common/hooks/useExternalLinks/types'
import { EditRFIResponseModalData } from './components/RFITrackerTable/hooks/useExpandOptions/components/EditResponseModal'

type RFIQuery = QueryResult<GetRequestsForInformationQuery>

export type RequestForInformation = NonNullable<
  RFIQuery['data']
>['requestsForInformation']['records'][number]

export type ResponseDiscipline =
  RequestForInformation['responseDisciplines'][number]

export type Response = RequestForInformation['responses'][number]

type RFIMetadataQuery = QueryResult<
  GetRfiTrackerTableMetadataQuery,
  GetRfiTrackerTableMetadataQueryVariables
>

export type MetadataResponseDiscipline = NonNullable<
  RFIMetadataQuery['data']
>['project']['rfiResponseDisciplines'][number]

const { Provider, useContextFn } = createStrictContext<
  {
    records: RequestForInformation[]
    refetch: RFIQuery['refetch']
    fetching: RFIQuery['loading']
    metadata: Pick<RFIMetadataQuery, 'loading' | 'refetch'> &
      Omit<NonNullable<RFIMetadataQuery['data']>['project'], '__typename'>
    edit: EditRequestForInformationMutationFn
    selectOptions: {
      organizationOptions: DefaultOptionType[]
    }
    draftSubmitModal: UseCIMSModalValues<DraftSubmitRFIModalData>
    disciplineResponseModal: UseCIMSModalValues<DisciplineResponseModalData>
    revertRFIModal: UseCIMSModalValues<RequestForInformation>
    editResponseModal: UseCIMSModalValues<EditRFIResponseModalData>
  } & UseSharePointExternalLinks<RequestForInformation>
>('RFITrackerContext')

export const useRFITrackerContext = useContextFn

export const RFITrackerProvider: React.FC = ({ children }) => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const {
    refetch,
    loading: fetching,
    data,
  } = useGetRequestsForInformationQuery({
    ...DefaultQueryHookOptions,
    skip: !projectId,
    variables: {
      projectId: projectId!,
      requestForInformationListInput: {
        projectId: {
          equals: projectId,
        },
      },
    },
  })
  const {
    loading: metaLoading,
    refetch: metaRefetch,
    data: metadata,
  } = useGetRfiTrackerTableMetadataQuery({
    ...DefaultQueryHookOptions,
    skip: !projectId,
    variables: {
      globalId: projectId!,
    },
  })
  const { rfiOrganizations, rfiResponseDisciplines, rfiQuickLinks } =
    metadata?.project ?? {}
  const [edit] = useEditRequestForInformationMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editRequestForInformation: {
        requestForInformation: { formattedNumber },
      },
    }) => simpleEditNotification(formattedNumber),
  })
  const organizationOptions = useOptions({
    items: rfiOrganizations,
    value: 'id',
    label: 'name',
    sort: 'name',
    tooltip: ({ description }) => description,
  })
  const draftSubmitModal = useCIMSModalValues<DraftSubmitRFIModalData>()
  const disciplineResponseModal =
    useCIMSModalValues<DisciplineResponseModalData>()
  const revertRFIModal = useCIMSModalValues<RequestForInformation>()
  const editResponseModal = useCIMSModalValues<EditRFIResponseModalData>()
  const externalLinks = useExternalLinks<RequestForInformation>({
    dataFn: ({ id, externalLinks, sharePointResourceWebUrl, number }) => ({
      recordId: id,
      externalLinks,
      folder: number.toString(),
      resource: { webUrl: sharePointResourceWebUrl },
    }),
    parent: SharePointParentFolder.RequestForInformation,
    refetch,
  })
  return (
    <Provider
      value={{
        ...externalLinks,
        refetch,
        fetching,
        records: data?.requestsForInformation?.records ?? [],
        metadata: {
          loading: metaLoading,
          refetch: metaRefetch,
          rfiOrganizations: rfiOrganizations ?? [],
          rfiResponseDisciplines: rfiResponseDisciplines ?? [],
          rfiQuickLinks: rfiQuickLinks ?? [],
        },
        edit,
        selectOptions: {
          organizationOptions,
        },
        draftSubmitModal,
        disciplineResponseModal,
        revertRFIModal,
        editResponseModal,
      }}
    >
      {children}
    </Provider>
  )
}
